import ExtensionHighlight from '@tiptap/extension-highlight';
import ExtensionPlaceholder from '@tiptap/extension-placeholder';
import { type EditorView } from '@tiptap/pm/view';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import TiptTap from '@tiptap/starter-kit';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { Toolbar } from './Toolbar';
import { type RichTextInputProps } from './types';

const EDITOR_ATTRIBUTES = {
  class: cn(
    'outline-none max-h-35 overflow-y-auto flex-1 font-light text-sm leading-snug',
    'w-full [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5',
    '[&_a]:underline [&_a]:text-teal-700 [&_a]:cursor-text',
    '[&_p.is-editor-empty:first-child::before]:text-gray-400',
    '[&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
    '[&_p.is-editor-empty:first-child::before]:float-left',
    '[&_p.is-editor-empty:first-child::before]:h-0',
    '[&_p.is-editor-empty:first-child::before]:pointer-events-none'
  ),
};

const handleEnterKey = (
  view: EditorView,
  event: KeyboardEvent,
  onSubmit: () => void
): boolean | void => {
  if (event.key !== 'Enter' || event.shiftKey) return;

  const { $from } = view.state.selection;
  const ancestorNodes = Array.from({ length: $from.depth }, (_, depthIndex) =>
    $from.node(depthIndex + 1)
  );
  const isInsideListItem = ancestorNodes.some(node => node.type.name === 'listItem');
  if (isInsideListItem) return false;

  event.preventDefault();
  onSubmit();

  const emptyParagraph = view.state.schema.nodes.paragraph.create();
  view.dispatch(view.state.tr.replaceWith(0, view.state.doc.content.size, emptyParagraph));

  return true;
};

export const RichTextInput = memo(function RichTextInput({
  value,
  isRichTextMode,
  initialContent,
  onHtmlChange,
  onEmptyChange,
  onSubmit,
}: RichTextInputProps): React.ReactElement {
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const onEmptyChangeRef = useRef(onEmptyChange);
  onEmptyChangeRef.current = onEmptyChange;

  const handleOnUpdate = useCallback(
    ({ editor: updatedEditor }: { editor: Editor }) => {
      onHtmlChange(updatedEditor.getHTML());
      onEmptyChange?.(updatedEditor.isEmpty);
    },
    [onHtmlChange, onEmptyChange]
  );

  const handleOnCreate = useCallback(
    ({ editor: createdEditor }: { editor: Editor }) => {
      onEmptyChange?.(createdEditor.isEmpty);
    },
    [onEmptyChange]
  );

  const handleOnKeyDown = useCallback(
    (view: EditorView, event: KeyboardEvent): boolean | void =>
      handleEnterKey(view, event, onSubmit),
    [onSubmit]
  );

  const handleOnClick = useCallback(
    (_view: EditorView, _pos: number, event: MouseEvent): boolean | void => {
      const target = event.target as HTMLElement;
      if (target.closest('a')) {
        event.preventDefault();
        return true;
      }
      return false;
    },
    []
  );

  const editor = useEditor({
    extensions: [
      TiptTap.configure({
        heading: false,
        blockquote: false,
        horizontalRule: false,
        codeBlock: false,
        hardBreak: {},
        link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
      }),
      ExtensionPlaceholder.configure({ placeholder: t('Chat.Messages.placeholder') }),
      ExtensionHighlight.configure({ multicolor: true }),
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        ...EDITOR_ATTRIBUTES,
      },
      handleKeyDown: handleOnKeyDown,
      handleClick: handleOnClick,
    },
    onUpdate: handleOnUpdate,
    onCreate: handleOnCreate,
  });

  useEffect(() => {
    if (!editor) return;

    const currentHtml = editor.getHTML();

    if (value === '' && !editor.isEmpty) {
      editor.commands.clearContent(true);
      return;
    }

    if (value && value !== currentHtml) {
      editor.commands.setContent(value, { emitUpdate: false });
      onEmptyChangeRef.current?.(editor.isEmpty);
      editor.commands.focus('end');
    }
  }, [value, editor]);

  if (!editor) return <div className="flex-1 min-h-6" />;

  return (
    <div className="flex flex-1 flex-col gap-1 w-full">
      {isRichTextMode && <Toolbar editor={editor} />}
      <EditorContent
        editor={editor}
        className="flex flex-1 overflow-hidden bg-gray-100 px-4 py-2 rounded-lg w-full border border-plum-200"
      />
    </div>
  );
});
